import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Box, Center, Spinner } from '@chakra-ui/react';
import { PaginationState, SortingState } from '@tanstack/react-table';

import { EmptyState, ScheduledGift } from '@aslanpay/portal-common-lib';

import CancelScheduledGiftModal from 'app/components/Gifting/CancelScheduledGiftModal';
import ScheduledGiftDetailsModal from 'app/components/Gifting/ScheduledGiftDetailsModal';
import { Table } from 'app/components/UI/Table';
import { defaultPageSize } from 'app/constants/table';
import useScheduledGiftActions from 'app/hooks/useScheduledGiftActions';
import useScheduledGifts from 'app/hooks/useScheduledGifts';
import getScheduledGiftsColumns, { scheduledGiftSortColumns } from 'app/utils/table/scheduledGifts';

/** The selected company's scheduled gifts, a page at a time from the API. */
function ScheduledGiftsTable() {
  const { companyId = '' } = useParams();
  const [giftToView, setGiftToView] = useState<ScheduledGift | undefined>();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });

  const sortBy = scheduledGiftSortColumns[sorting[0]?.id];
  const getSortDirection = () => {
    if (sortBy) {
      return sorting[0].desc ? 'DESC' : 'ASC';
    }
    return undefined;
  };

  const { data, isLoading, isError } = useScheduledGifts.useFetchScheduledGifts({
    companyId,
    page: pageIndex,
    size: pageSize,
    sortBy,
    sort: getSortDirection(),
  });

  const {
    editGift,
    retryGift,
    isRetrying,
    sendAgain,
    resendingGiftId,
    giftToCancel,
    askToCancel,
    closeCancel,
    confirmCancel,
    isCancelling,
  } = useScheduledGiftActions({ onCancelled: () => setGiftToView(undefined) });

  const renderList = () => {
    if (isError) {
      return <EmptyState full text="Sorry, we failed to load the scheduled gifts" />;
    }
    if (isLoading) {
      return (
        <Center data-testid="scheduled-gifts-loading">
          <Spinner color="brand.purple" size="lg" />
        </Center>
      );
    }
    if (data?.list.length) {
      return (
        <Table<ScheduledGift>
          columns={getScheduledGiftsColumns(
            {
              onView: setGiftToView,
              onEdit: editGift,
              onCancel: askToCancel,
              onRetry: (gift) => retryGift(gift.id),
              onSendAgain: sendAgain,
            },
            { isRetrying, isResending: !!resendingGiftId }
          )}
          data={data.list}
          total={data.total}
          pageNumber={pageIndex}
          pageSize={pageSize}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={(updater) => {
            setSorting(updater);
            setPagination((pagination) => ({ ...pagination, pageIndex: 0 }));
          }}
          getRowId={(gift) => gift.id}
        />
      );
    }
    return <EmptyState full text="There are no scheduled gifts to display" />;
  };

  return (
    <Box data-testid="scheduled-gifts-table">
      {renderList()}
      {giftToView && (
        <ScheduledGiftDetailsModal
          isOpen
          onClose={() => setGiftToView(undefined)}
          gift={giftToView}
          onEdit={editGift}
          onCancel={askToCancel}
          onRetry={(gift) => retryGift(gift.id)}
          onSendAgain={sendAgain}
          isRetrying={isRetrying}
          isResending={resendingGiftId === giftToView.id}
        />
      )}
      <CancelScheduledGiftModal
        isOpen={!!giftToCancel}
        onClose={closeCancel}
        gift={giftToCancel}
        isCancelling={isCancelling}
        onConfirm={confirmCancel}
      />
    </Box>
  );
}

export default ScheduledGiftsTable;
